import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Avatar,
  Grid,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  CameraAlt as CameraIcon,
} from '@mui/icons-material';
import { companiesAPI as companyAPI, authAPI } from '../services';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin as isSuperAdminCheck } from '../utils/userPermissions';
import { getCompanyTypeLabel, getCompanyTypeColor } from '../utils/companyTypes';
import { getTimezoneAbbreviation } from '../utils/timezones';
import config from '../config';

interface Company {
  companyid: string;
  companyname: string;
  companytype: string;
  logo?: string;
  style?: string;
  prefixword?: string;
  timezone: string;
  remark?: string;
  pourcode?: string;
  calibrationcode?: string;
  reportprefix?: string;
  reportno?: string;
  dateformat?: string;
  tempformat?: string;
  evaformat?: string;
  strengthformat?: string;
  densityformat?: string;
  maturitymethod?: string;
  defaultmaturitymethod?: string;
  language?: string;
  defaultcalibrationspecifiedtemp?: number;
  defaultcalibrationinternaltemp?: number;
  status?: string;
  loggercalibrationfrequency?: number;
  productcalibrationfrequency?: number;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  hasmass?: boolean;
  hasmaturity?: boolean;
  hasmatchcuring?: boolean;
  hasweather?: boolean;
  haseva?: boolean;
  hasexternalth?: boolean;
  hasextend?: boolean;
  hasb80?: boolean;
  hassteam?: boolean;
  dataapi?: boolean;
  contactname?: string;
  contactemail?: string;
  windspeedformat?: string;
  isactive: boolean;
  createtime?: string;
}

const MAX_LOGO_MB = 5;

// Effective licensed pour functions, mirroring backend monitoring_access:
// all flags NULL = never configured = full access; otherwise NULL is off
// except External T&H (flag postdates the others, NULL = granted), and
// prerequisites are pulled in (Maturity/TMC/Eva need Temperature Only,
// TMC needs Maturity).
const effectiveLicensing = (company: Company) => {
  const raw = [
    company.hasmass,
    company.hasmaturity,
    company.hasmatchcuring,
    company.hasweather,
    company.haseva,
    company.hasexternalth,
  ];
  const unconfigured = raw.every((v) => v === null || v === undefined);
  const mass = !!(company.hasmass || company.hasmaturity || company.hasmatchcuring || company.haseva);
  return {
    unconfigured,
    hasmass: unconfigured || mass,
    hasmaturity: unconfigured || !!(company.hasmaturity || company.hasmatchcuring),
    hasmatchcuring: unconfigured || !!company.hasmatchcuring,
    hasweather: unconfigured || !!company.hasweather,
    haseva: unconfigured || !!company.haseva,
    hasexternalth: unconfigured || company.hasexternalth !== false,
  };
};

const CompanyView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Logo-upload feedback is separate from the page-level `error` so a failed
  // upload doesn't blank the whole page (the early return keys on `error`).
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const userType = (user?.UserType || '').trim();
  const isSuperAdmin = isSuperAdminCheck(user);
  const isOwnCompany = String(user?.CompanyID || '0').trim() === String(id).trim();
  // Logo upload lives here (was a separate General Settings page): super
  // admins on any company, client admins on their own.
  const canEditLogo = isSuperAdmin || (isOwnCompany && userType === '02');

  // Format-setting codes → labels (same wording as the CompanyEdit selects).
  // Falls back to the raw value for unrecognised codes.
  const formatLabel = (kind: string, value?: string): string => {
    if (!value) return '-';
    const maps: Record<string, Record<string, string>> = {
      dateformat: {
        '01': t('companyEdit.dateFormatAU', 'DD/MM/YYYY (AU)'),
        '02': t('companyEdit.dateFormatUS', 'MM/DD/YYYY (US)'),
      },
      tempformat: {
        '01': t('companyEdit.celsius', 'Celsius (°C)'),
        '02': t('companyEdit.fahrenheit', 'Fahrenheit (°F)'),
      },
      evaformat: {
        '01': t('companyEdit.evaMetric', 'Metric (kg/m²/hr)'),
        '02': t('companyEdit.evaImperial', 'Imperial (lb/ft²/hr)'),
      },
      strengthformat: {
        '01': t('companyEdit.strengthMpa', 'MPa'),
        '02': t('companyEdit.strengthPsi', 'PSI'),
        '03': t('companyEdit.strengthNmm', 'N/mm²'),
      },
      densityformat: {
        '01': t('companyEdit.densityKg', 'kg/m³'),
        '02': t('companyEdit.densityLbYd', 'lb/yd³'),
        '03': t('companyEdit.densityLbFt', 'lb/ft³'),
      },
      windspeedformat: {
        '01': t('companyEdit.windKmh', 'km/h'),
        '02': t('companyEdit.windMph', 'mph'),
      },
      language: {
        en: t('companyEdit.english', 'English'),
        zh: t('companyEdit.chinese', 'Chinese'),
        es: t('companyEdit.spanish', 'Spanish'),
        fr: t('companyEdit.french', 'French'),
      },
    };
    return maps[kind]?.[value] || value;
  };

  const handleLogoClick = () => {
    if (canEditLogo) {
      fileInputRef.current?.click();
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !id) return;

    if (!file.type.startsWith('image/')) {
      setUploadError(t('generalSettings.logoTypeError', 'Please choose an image file (PNG, JPG, GIF or WebP).'));
      return;
    }
    if (file.size > MAX_LOGO_MB * 1024 * 1024) {
      setUploadError(t('generalSettings.logoSizeError', { defaultValue: 'File too large. Maximum size is {{mb}}MB.', mb: MAX_LOGO_MB }));
      return;
    }

    try {
      setUploading(true);
      setUploadError(null);
      await companyAPI.uploadLogo(id, file);
      if (isOwnCompany) {
        // The header bar shows this logo from the cached user — refresh it
        // and reload so the change is visible everywhere immediately.
        try {
          const me = await authAPI.getCurrentUser();
          localStorage.setItem('user', JSON.stringify(me));
        } catch {
          /* header updates on next login if the refresh fails */
        }
        window.location.reload();
        return;
      }
      await fetchCompany(id);
    } catch (err: any) {
      setUploadError(err?.response?.data?.detail || t('generalSettings.logoUploadFailed', 'Failed to upload logo'));
    } finally {
      setUploading(false);
    }
  };

  const fetchCompany = useCallback(async (companyId: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await companyAPI.getCompanyById(companyId);
      setCompany(response);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 403) {
        setError(t('companyView.accessDeniedPermission'));
      } else if (status === 404) {
        setError(t('companyView.companyNotFound'));
      } else {
        setError(detail || err?.message || t('companyView.fetchError'));
      }
      console.error('Error fetching company:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin && !isOwnCompany) {
      setError(t('companyView.accessDeniedOwn'));
      setLoading(false);
      return;
    }

    if (id) {
      fetchCompany(id);
    }
  }, [id, isSuperAdmin, isOwnCompany, fetchCompany]);

  const handleBack = () => {
    if (isSuperAdmin) {
      navigate('/companies');
    } else {
      navigate(-1);
    }
  };

  const handleEdit = () => {
    navigate(`/companies/${id}/edit`);
  };

  if (!isSuperAdmin && !isOwnCompany) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('companyView.accessDeniedOwn')}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          {t('common.back')}
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || t('companyView.companyNotFound')}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          {isSuperAdmin ? t('companies.backToCompanies') : t('common.back')}
        </Button>
      </Box>
    );
  }

  const logoPath = company.logo
    ? `${config.apiBaseUrl}/files/company-logos/${company.companyid}/${company.logo}`
    : null;

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            {isSuperAdmin ? t('companies.backToCompanies') : t('common.back')}
          </Button>
        </Box>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            {t('companyView.editCompany')}
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Feature Configuration - First, like edit page */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('companyView.licensedPourFunctions', 'Licensed Pour Functions')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {(() => {
                const lic = effectiveLicensing(company);
                const functions: { on: boolean; label: string }[] = [
                  { on: lic.hasmass, label: t('companyView.funcTemperatureOnly', 'Temperature Only (Mass Concrete)') },
                  { on: lic.hasmaturity, label: t('companyView.funcConcreteMaturity', 'Concrete Maturity') },
                  { on: lic.hasmatchcuring, label: t('companyView.funcTmc', 'Temperature Match Curing (TMC)') },
                  { on: lic.hasweather, label: t('companyView.funcWeather', 'Onsite Weather Station') },
                  { on: lic.haseva, label: t('companyView.funcEva', 'Evaporation Rate Calculations') },
                  { on: lic.hasexternalth, label: t('companyView.funcExternalTH', 'External Temperature & Humidity') },
                ];
                return (
                  <>
                    {lic.unconfigured && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {t(
                          'companyView.legacyDefaultFullAccess',
                          'Licensing not configured: full access (legacy default)',
                        )}
                      </Typography>
                    )}
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {functions.map(({ on, label }) => (
                        <Chip
                          key={label}
                          label={label}
                          color={on ? 'primary' : 'default'}
                          variant={on ? 'filled' : 'outlined'}
                          size="small"
                          sx={on ? undefined : { opacity: 0.6 }}
                        />
                      ))}
                    </Box>
                  </>
                );
              })()}

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }} color="text.secondary">
                {t('companyView.otherFeatureFlags', 'Other Feature Flags')}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {company.hasextend && (
                  <Chip label={t('companyView.extendedMonitoring')} color="primary" size="small" />
                )}
                {company.hasb80 && (
                  <Chip label={t('companyView.b80Specification')} color="primary" size="small" />
                )}
                {company.hassteam && (
                  <Chip label={t('companyView.steamCuring')} color="primary" size="small" />
                )}
                {company.dataapi && (
                  <Chip label={t('companyView.dataApi')} color="secondary" size="small" />
                )}
                <Chip
                  label={company.isactive ? t('common.active') : t('common.inactive')}
                  color={company.isactive ? 'success' : 'default'}
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Basic Information */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('companyView.basicInformation')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {/* Company Header with Logo */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoChange}
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
              />
              <Box display="flex" alignItems="center" mb={3}>
                <Box
                  onClick={handleLogoClick}
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    cursor: canEditLogo ? 'pointer' : 'default',
                    mr: 2,
                    '&:hover .logo-overlay': canEditLogo ? { opacity: 1 } : {},
                  }}
                >
                  <Avatar
                    src={logoPath || undefined}
                    alt={`${company.companyname} logo`}
                    sx={{ width: 80, height: 80, bgcolor: 'primary.main', border: '1px solid', borderColor: 'grey.700' }}
                    imgProps={{ style: { objectFit: 'contain' } }}
                  >
                    <BusinessIcon fontSize="large" />
                  </Avatar>
                  {canEditLogo && (
                    <Box
                      className="logo-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        borderRadius: '50%',
                        bgcolor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                      }}
                    >
                      {uploading ? (
                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                      ) : (
                        <CameraIcon sx={{ color: '#fff', fontSize: 28 }} />
                      )}
                    </Box>
                  )}
                </Box>
                <Box>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {company.companyname}
                  </Typography>
                  <Chip
                    label={getCompanyTypeLabel(t, company.companytype)}
                    color={getCompanyTypeColor(company.companytype)}
                    size="small"
                  />
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.companyName')}
                    </Typography>
                    <Typography variant="body1">
                      {company.companyname}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.companyType')}
                    </Typography>
                    <Typography variant="body1">
                      {getCompanyTypeLabel(t, company.companytype)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.timezone')}
                    </Typography>
                    <Typography variant="body1">
                      {getTimezoneAbbreviation(company.timezone)}
                      {company.timezone && company.timezone !== getTimezoneAbbreviation(company.timezone) && (
                        <Typography variant="body2" color="textSecondary" component="span" sx={{ ml: 1 }}>
                          ({company.timezone})
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                </Grid>
                {company.address && (
                  <Grid size={12}>
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">
                        {t('companyView.address')}
                      </Typography>
                      <Typography variant="body1">
                        {company.address}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.phone')}
                    </Typography>
                    <Typography variant="body1">
                      {company.phone || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.email')}
                    </Typography>
                    <Typography variant="body1">
                      {company.email || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.website')}
                    </Typography>
                    <Typography variant="body1">
                      {company.website ? (
                        <a href={company.website} target="_blank" rel="noopener noreferrer">
                          {company.website}
                        </a>
                      ) : '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.contactName')}
                    </Typography>
                    <Typography variant="body1">
                      {company.contactname || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.contactEmail')}
                    </Typography>
                    <Typography variant="body1">
                      {company.contactemail || '-'}
                    </Typography>
                  </Box>
                </Grid>
                {company.remark && (
                  <Grid size={12}>
                    <Box>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {t('companyView.remark')}
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        backgroundColor: 'grey.50', 
                        p: 2, 
                        borderRadius: 1,
                        whiteSpace: 'pre-wrap'
                      }}>
                        {company.remark}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* General Settings */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('companyView.generalSettings')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.clientCode')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {company.prefixword || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.maturityMethod')}
                    </Typography>
                    <Typography variant="body1">
                      {company.maturitymethod || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.defaultMaturityMethod')}
                    </Typography>
                    <Typography variant="body1">
                      {company.defaultmaturitymethod || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.reportPrefix')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {company.reportprefix || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.defaultCalibSpecifiedTemp')}
                    </Typography>
                    <Typography variant="body1">
                      {company.defaultcalibrationspecifiedtemp !== null && company.defaultcalibrationspecifiedtemp !== undefined 
                        ? `${company.defaultcalibrationspecifiedtemp}°` : '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.defaultCalibInternalTemp')}
                    </Typography>
                    <Typography variant="body1">
                      {company.defaultcalibrationinternaltemp !== null && company.defaultcalibrationinternaltemp !== undefined 
                        ? `${company.defaultcalibrationinternaltemp}°` : '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.loggerCalibFrequency')}
                    </Typography>
                    <Typography variant="body1">
                      {company.loggercalibrationfrequency || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.productCalibFrequency')}
                    </Typography>
                    <Typography variant="body1">
                      {company.productcalibrationfrequency || '-'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.language')}
                    </Typography>
                    <Typography variant="body1">
                      {formatLabel('language', company.language)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Units of Measure Settings */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('companyView.unitsOfMeasure')}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.dateFormat')}
                    </Typography>
                    <Typography variant="body1">
                      {formatLabel('dateformat', company.dateformat)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.temperatureFormat')}
                    </Typography>
                    <Typography variant="body1">
                      {formatLabel('tempformat', company.tempformat)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.evaFormat')}
                    </Typography>
                    <Typography variant="body1">
                      {formatLabel('evaformat', company.evaformat)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.strengthFormat')}
                    </Typography>
                    <Typography variant="body1">
                      {formatLabel('strengthformat', company.strengthformat)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.densityFormat')}
                    </Typography>
                    <Typography variant="body1">
                      {formatLabel('densityformat', company.densityformat)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      {t('companyView.windSpeedFormat')}
                    </Typography>
                    <Typography variant="body1">
                      {formatLabel('windspeedformat', company.windspeedformat)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Logo-upload feedback (kept separate from the page-level error) */}
      <Snackbar
        open={uploadError !== null}
        autoHideDuration={5000}
        onClose={() => setUploadError(null)}
      >
        <Alert severity="error" onClose={() => setUploadError(null)} variant="filled">
          {uploadError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyView;
